import { FabricImage } from "../../objects";
import Layer from "../Layer";
import { ImageLayerProps, ImageProps } from "./interfaces";
import { ImageLoader, TaskManager } from "../../helpers";
import {
  ImageFilterProps,
  ImageFilterType,
  ImageStrokeEffectProps,
  ImageStrokeEffectType,
  LayerCustomType,
} from "../../interfaces";
import { createImageObject, fitObject, pickProps } from "../../utils";

const DefaultImageSrc = "/images/default_image.png";

export default class ImageLayer extends Layer {
  protected static customType: LayerCustomType = LayerCustomType.Image;
  protected static defaultImageSrc: string = DefaultImageSrc;

  name: string = "Image layer";
  protected imageObject: FabricImage = createImageObject();
  protected abortController?: AbortController;

  constructor(options: Partial<ImageLayerProps> = {}) {
    const {
      transformSettings = {},
      typeSettings = {},
      ...layerProps
    } = options;
    super();

    this.imageObject.set(typeSettings);
    this.insertAt(0, this.imageObject);

    this.set(transformSettings);
    this.set(layerProps);

    TaskManager.enqueue(this._id, async () => {
      await this.loadImage(typeSettings.src);
    });
  }

  protected rerender() {
    super.rerender();
    if (this.imageObject.group) {
      fitObject(this.imageObject, this, this.imageObject.objectFit);
    }
    this.canvas?.requestRenderAll();
  }

  toObject() {
    const object = super.toObject();
    const imageProps = this.getImageProps();
    return { ...object, typeSettings: imageProps };
  }

  async loadImage(src?: Blob | string): Promise<string> {
    this.imageObject.set({ originalSrc: src });

    const defaultImageSrc = (this.constructor as typeof ImageLayer)
      .defaultImageSrc;

    const srcToLoad =
      src instanceof Blob ? URL.createObjectURL(src) : src || defaultImageSrc;

    try {
      const imageElement = await ImageLoader.load(
        srcToLoad,
        this.imageObject.filter,
        this.imageObject.strokeEffect,
      );
      if (!imageElement) return "";
      this.imageObject.setElement(imageElement);
      this.rerender();
      return srcToLoad;
    } catch (error) {
      console.error(error);
      return "";
    }
  }

  async loadFilter(filter: ImageFilterProps = { type: ImageFilterType.None }) {
    const props = { filter };
    this.imageObject.set(props);
    this.loadImage(this.imageObject.originalSrc);
    this.rerender();
  }

  async loadStrokeEffect(
    strokeEffect: ImageStrokeEffectProps = {
      type: ImageStrokeEffectType.None,
      color: "",
      width: 0,
    },
  ) {
    const props = { strokeEffect };
    this.imageObject.set(props);
    this.loadImage(this.imageObject.originalSrc);
    this.rerender();
  }

  setImageProps(props: Partial<ImageProps>) {
    this.imageObject.set(props);
    this.rerender();
  }

  getImageProps(key?: string) {
    if (key) {
      return this.imageObject.get(key);
    } else {
      const imageProps = pickProps(this.imageObject, [
        "filter",
        "strokeEffect",
        "objectFit",
        "maskImagePath",
        "maskImageSrc",
        "libraryItemId",
      ]);
      return imageProps;
    }
  }
}

export * from "./interfaces";
